import AccordionWithMediaSection from "@/components/Home/v2/AccordionWithMediaSection";
import AiTeamSection from "@/components/Home/v2/AiTeamSection";
import CenteredMediaBleedSection from "@/components/Home/v2/CenteredMediaBleedSection";
import ColoredOutcomeCardsSection from "@/components/Home/v2/ColoredOutcomeCardsSection";
import CtaBannerV2 from "@/components/Home/v2/CtaBannerV2";
import DarkMediaChecklistSection from "@/components/Home/v2/DarkMediaChecklistSection";
import DarkNumberedStepsSection from "@/components/Home/v2/DarkNumberedStepsSection";
import DarkTwoColCardsSection from "@/components/Home/v2/DarkTwoColCardsSection";
import IntegrationLogoArcSection from "@/components/Home/v2/IntegrationLogoArcSection";
import FaqV2 from "@/components/Home/v2/FaqV2";
import FooterV2 from "@/components/Home/v2/FooterV2";
import ProductCardGridSection from "./ProductCardGridSection";
import ThreePillarCardsSection from "./ThreePillarCardsSection";
import ScrollAnimatedSplitHeadingSection from "./ScrollAnimatedSplitHeadingSection";
import ShipFasterWithAccordionSection from "./ShipFasterWithAccordionSection";
import AlternatingProductsSection from "./AlternatingProductsSection";
import StickyScrollProductsSection from "./StickyScrollProductsSection";
import SplitOutcomeCardsSection from "./SplitOutcomeCardsSection";
import TemplateShowcaseSection from "./TemplateShowcaseSection";
import NavbarV2 from "@/components/Home/v2/NavbarV2";
import StoriesMarqueeV2 from "@/components/Home/v2/StoriesMarqueeV2";
import GraphyHead from "@/components/organisms/GraphyHead";
import { ProductV2DataType, ProductV2FaqSection, ProductV2Section } from "@/shared/data/product.v2.data";
import ProductHeroV2 from "./ProductHeroV2";
import Head from "next/head";

/**
 * ProductV2 — orchestrator for a product page rebuilt on the HomeV2
 * design system.
 *
 * Layout: Navbar → hero → heterogeneous ordered section stack (accordion,
 * card-grid, ai-team, stories, faq) → CTA banner → Footer. The section
 * list is driven entirely by data; each entry is a `{ kind, data }`
 * discriminated union dispatched to the matching HomeV2 component.
 */

export type ProductV2Props = {
  data: ProductV2DataType;
  countryCode: string;
  /** Overrides the default NavbarV2 header — used by ad-only page variants
   *  (e.g. /coaching) that swap the navbar for a promo banner. */
  navSlot?: React.ReactNode;
};

const renderSection = (section: ProductV2Section, idx: number) => {
  switch (section.kind) {
    case "accordion":
      return (
        <AccordionWithMediaSection
          key={`${section.kind}-${section.data.eyebrow ?? idx}`}
          {...section.data}
        />
      );
    case "card-grid":
      return (
        <ProductCardGridSection
          key={`${section.kind}-${section.data.eyebrow ?? idx}`}
          {...section.data}
        />
      );
    case "ai-team":
      return (
        <AiTeamSection
          key={`${section.kind}-${section.data.eyebrow ?? idx}`}
          {...section.data}
        />
      );
    case "stories":
      return (
        <StoriesMarqueeV2
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "faq":
      return <FaqV2 key={`${section.kind}-${idx}`} {...section.data} />;
    case "centered-media":
      return (
        <CenteredMediaBleedSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "dark-two-col-cards":
      return (
        <DarkTwoColCardsSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "dark-numbered-steps":
      return (
        <DarkNumberedStepsSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "dark-media-checklist":
      return (
        <DarkMediaChecklistSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "integration-logo-arc":
      return (
        <IntegrationLogoArcSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "colored-outcome-cards":
      return (
        <ColoredOutcomeCardsSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "three-pillar-cards":
      return (
        <ThreePillarCardsSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "scroll-animated-heading":
      return (
        <ScrollAnimatedSplitHeadingSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "ship-faster-with-accordion":
      return (
        <ShipFasterWithAccordionSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "alternating-products":
      return (
        <AlternatingProductsSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "sticky-scroll-products":
      return (
        <StickyScrollProductsSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "split-outcome-cards":
      return (
        <SplitOutcomeCardsSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
    case "template-showcase":
      return (
        <TemplateShowcaseSection
          key={`${section.kind}-${idx}`}
          {...section.data}
        />
      );
  }
};

const ProductV2 = ({ data, countryCode, navSlot }: ProductV2Props) => {
  // Collect all FAQ items from sections tagged with kind:"faq" and build
  // a FAQPage schema so AI systems can surface answers directly.
  const faqItems = data.sections
    .filter((s): s is { kind: "faq"; data: ProductV2FaqSection } => s.kind === "faq")
    .flatMap((s) => s.data.items);

  const faqSchema = faqItems.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      }
    : null;

  return (
    <>
      <GraphyHead {...data.seoData} countryCode={countryCode} />
      {faqSchema && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        </Head>
      )}
      <div className="home-v2-scope relative">
        {navSlot ?? <NavbarV2 />}
        <main className="relative z-10 flex flex-col">
          <ProductHeroV2 {...data.hero} />
          {data.sections.map((section, idx) => renderSection(section, idx))}
          <CtaBannerV2 {...data.ctaBanner} />
        </main>
      </div>
      <FooterV2 {...data.footer} />
    </>
  );
};

export default ProductV2;
